// The wrap-up form (disposition bar) as pure functions: defaults, validation, and the DispositionRequest it sends.
// Unit-tested in tests/fd-dialer.test.ts.
import type { Business, DispositionCode, DispositionRequest, ObjectionCategory } from '../../../src/shared/api';
import { DISPOSITION_META } from './labels';
import { isSafeEmail } from './script';
import { callbackProblem } from './time';

export interface WrapUp {
  code: DispositionCode | null;
  spokeToDm: boolean | null;          // null = the default for the chosen code
  objection: ObjectionCategory | '';
  note: string;
  callbackAt: string | null;          // ISO UTC; chosen in the prospect's zone
  dmName: string;
  dmRole: string;
  gatekeeperName: string;
  bestTimeNote: string;
  email: string;
  vmLiveRead: boolean;                // the live voicemail card was opened on this call
  callbackEarliestAt: string | null;  // the server's earliest allowed callback after a 400 callback_not_allowed (ISO UTC)
}

export function emptyWrap(b: Business | null | undefined): WrapUp {
  return {
    code: null, spokeToDm: null, objection: '', note: '', callbackAt: null,
    dmName: b?.dmName ?? '', dmRole: b?.dmRole ?? '', gatekeeperName: b?.gatekeeperName ?? '',
    bestTimeNote: b?.bestTimeNote ?? '', email: b?.email ?? '', vmLiveRead: false, callbackEarliestAt: null,
  };
}

export function effectiveSpokeToDm(w: WrapUp): boolean {
  if (w.spokeToDm !== null) return w.spokeToDm;
  return w.code ? DISPOSITION_META[w.code].spokeToDm : false;
}

/** What stops this wrap-up from being saved, in one sentence; null = ready. */
export function wrapProblem(w: WrapUp, ctx: { meetingBooked: boolean }, now = new Date()): string | null {
  if (!w.code) return 'Pick the outcome (the key is on each button).';
  if (w.code === 'vm_dropped') return 'Voicemail drops are logged automatically; pick another outcome.';
  if (DISPOSITION_META[w.code].needsCallback) {
    const p = callbackProblem(w.callbackAt ? new Date(w.callbackAt) : null, now);
    if (p) return p;
  }
  // PJ 2026-10-20: the server says the same (calls/disposition.ts MEETING_REQUIRED_MESSAGE).
  if (w.code === 'meeting_booked' && !ctx.meetingBooked) return 'Book it first (B): book a time, or pick the booking they made on the Calendly link.';
  if (w.email.trim() && !isSafeEmail(w.email.trim())) return "The email address doesn't look right.";
  return null;
}

/**
 * No-campaign pad calls (SPEC §19, 2026-10-02): what stops "Save to My leads" (saveLead true) or "Don't save" (false),
 * on top of wrapProblem; null = that button may save. There is no lead row, so a callback needs the number kept, and
 * since PJ 2026-10-20 so does a meeting (linked from the Calendly link, or booked elsewhere, in the Book panel).
 */
export function noCampaignProblem(w: WrapUp, saveLead: boolean): string | null {
  if (!saveLead && w.code === 'meeting_booked') return 'A meeting needs this number saved: press Save to My leads.';
  if (!saveLead && w.code && DISPOSITION_META[w.code].needsCallback) return 'A callback needs this number saved: use Save to My leads, or pick another outcome.';
  return null;
}

function changed(next: string, prev: string | null | undefined): string | undefined {
  const v = next.trim();
  return v && v !== (prev ?? '').trim() ? v : undefined;
}

/**
 * REVIEW-2026-10-01: one "Gatekeeper" button for both gatekeeper codes. It is logged as `gatekeeper_intel` when the setter
 * typed something new the gatekeeper gave (the decision-maker's name, their own name or a best time), else as
 * `gatekeeper_blocked`, so reports keep both counts. Any other code passes through unchanged.
 */
export function resolveOutcomeCode(w: WrapUp, business: Business | null | undefined): DispositionCode | null {
  if (w.code !== 'gatekeeper_intel' && w.code !== 'gatekeeper_blocked') return w.code;
  const gave = !!(changed(w.dmName, business?.dmName) || changed(w.gatekeeperName, business?.gatekeeperName) || changed(w.bestTimeNote, business?.bestTimeNote));
  return gave ? 'gatekeeper_intel' : 'gatekeeper_blocked';
}

/**
 * The request body for POST /api/calls/:id/disposition. Contact fields are sent only when the setter changed them
 * (the server copies given fields onto the business); callbackAt only for the callback outcomes; vmLive for vm_live.
 * spokeToDm follows the outcome (the "Spoke to the decision-maker" box is gone: the outcome implies it).
 */
export function buildDisposition(w: WrapUp, business: Business | null | undefined): DispositionRequest {
  const code = resolveOutcomeCode(w, business);
  if (!code) throw new Error('buildDisposition without a code');
  const req: DispositionRequest = { code, spokeToDm: effectiveSpokeToDm({ ...w, code }) };
  if (w.objection) req.objection = w.objection;
  if (w.note.trim()) req.note = w.note.trim();
  if (w.callbackAt && DISPOSITION_META[code].needsCallback) req.callbackAt = w.callbackAt;
  const dmName = changed(w.dmName, business?.dmName); if (dmName) req.dmName = dmName;
  const dmRole = changed(w.dmRole, business?.dmRole); if (dmRole) req.dmRole = dmRole;
  const gk = changed(w.gatekeeperName, business?.gatekeeperName); if (gk) req.gatekeeperName = gk;
  const best = changed(w.bestTimeNote, business?.bestTimeNote); if (best) req.bestTimeNote = best;
  const email = changed(w.email, business?.email); if (email) req.email = email;
  if (code === 'vm_live') req.vmLive = true;
  return req;
}

/** Outcome to pre-select when a call ends with nothing picked (the setter still confirms with Enter). */
export function defaultOutcome(args: { meetingBooked: boolean; vmLiveRead: boolean; answered: boolean }): DispositionCode | null {
  if (args.meetingBooked) return 'meeting_booked';
  if (args.vmLiveRead) return 'vm_live';
  if (!args.answered) return 'no_answer';
  return null;
}

/**
 * A disposition save the server refused because the call's outcome is already settled (calls/disposition.ts):
 * - 'logged': the call already has a final outcome (a voicemail drop, a DNC request, a booked meeting, or a save more
 *   than 10 minutes old). Nothing is owed; move on. `savedCode` is the outcome the server kept, when the code implies it.
 * - 'nothing': the call was never placed (409 not_placed), so there is nothing to log; the lead may be dialed again.
 * null = a real failure the setter has to retry.
 */
export type SettledOutcome = { kind: 'logged'; savedCode: DispositionCode | null } | { kind: 'nothing' };

export function settledOutcome(status: number, error: string | undefined): SettledOutcome | null {
  if (status !== 409) return null;
  switch (error) {
    case 'drop_final': return { kind: 'logged', savedCode: 'vm_dropped' };
    case 'dnc_final': return { kind: 'logged', savedCode: 'dnc_request' };
    case 'meeting_final': return { kind: 'logged', savedCode: 'meeting_booked' };
    case 'already_logged': return { kind: 'logged', savedCode: null };
    case 'not_placed': return { kind: 'nothing' };
    default: return null;
  }
}

/**
 * A 400 callback_not_allowed from POST /api/calls/:id/disposition (CORE, fix round 1): the callback time is outside
 * calling hours in a zone the number could be in (no holidays, Sundays only where set), or (PJ 2026-10-21) it would be a
 * 4th call to the number in 24 hours. The 4 h spacing, 2 a day and 3 a week no longer limit a callback time.
 * `details.earliestAt` is the earliest allowed time at or after the one asked for; it is absent when there is none in
 * the following 3 weeks. null = not this error.
 */
export function callbackRefusal(status: number, error: string | undefined, details: unknown): { earliestAt: string | null } | null {
  if (status !== 400 || error !== 'callback_not_allowed') return null;
  const raw = details && typeof details === 'object' ? (details as { earliestAt?: unknown }).earliestAt : undefined;
  const ok = typeof raw === 'string' && !Number.isNaN(Date.parse(raw));
  return { earliestAt: ok ? new Date(raw).toISOString() : null };
}
